import { useState } from 'react'
import { Card, Col, Dropdown, Button } from 'react-bootstrap'
import moment from 'moment'
import { Link, useHistory } from 'react-router-dom'
import TextInformationLine from '../text-information-line'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import useProvisionTwilioNumber from 'hooks/use-provision-twilio-number'
import useUserSubscriptionTracker from 'hooks/use-user-subscription-tracker'
import ReactivatePhoneNumberModal from '../reactivate-phone-number-modal'
import { useGetOrganizationUsersWithAppleIds } from 'hooks/use-get-organization-users-with-apple-ids'
import { useQueryClient } from 'react-query'
import { PhoneNumber } from 'interfaces/phone-number'
import { User } from 'interfaces/user'
import useUserVoiceIntegrity from 'hooks/use_user_voice_integrity'
import useUpdateAppleId from 'hooks/use-update-apple-id-details'
import { AppleId } from 'interfaces/apple-id'
import useCreateCancelTicket from 'hooks/use-create-cancel-ticket'
import toast from 'react-hot-toast'
import { shouldBlockGhostMessages } from 'utils/ghost-access'

const UserPhoneDetailsCardNew = ({
  appleId,
  service,
  worknumber,
  handleDeletePhoneNumber,
  deletePhoneNumber,
  userName,
  callRecordingEnabled,
  orgId,
  orgName,
  user,
  isInternalAdmin,
  redactMessageHistory,
  customerProductType,
}: {
  appleId?: AppleId
  service: string
  worknumber?: {
    id: string
    number: string
    created_at: string
    forwarding_number: string
  }
  handleDeletePhoneNumber: (id: string) => void
  deletePhoneNumber: { isLoading: boolean }
  userName?: string
  callRecordingEnabled?: boolean
  orgId?: string
  orgName?: string
  user?: User
  isInternalAdmin?: boolean
  redactMessageHistory?: boolean
  customerProductType?: string
}) => {
  const history = useHistory()
  const blockGhostMessages = shouldBlockGhostMessages(
    redactMessageHistory,
    customerProductType
  )
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const { data: appleIdDetails, error: appleIdError } = useGetAppleIdByUserId(
    user?.id || ''
  )
  const provisionTwilioNumber = useProvisionTwilioNumber()
  const userVoiceIntegrity = useUserVoiceIntegrity(parseInt(user?.id || ''))
  const { data: subscriptionData, isLoading: isLoadingSubscription } =
    useUserSubscriptionTracker(user?.id || '')
  const { data: usersWithAppleIdsData } = useGetOrganizationUsersWithAppleIds(
    orgId || '',
    !!orgId
  )
  const queryClient = useQueryClient()
  const { mutate: updateAppleId, isLoading: isUpdatingAppleId } =
    useUpdateAppleId()
  const createCancelTicket = useCreateCancelTicket()

  const handleProvisionNumber = () => {
    provisionTwilioNumber.mutate({ user_id: parseInt(user?.id || '') })
  }

  const handleReactivatePhoneNumber = () => {
    setShowReactivateModal(true)
  }

  const handleTwilioVoiceIntegrity = () => {
    userVoiceIntegrity.mutate()
  }
  const triggerUpdateAppleId = (updateParams: Partial<AppleId>) => {
    if (appleId && appleId.id !== undefined) {
      updateAppleId({
        id: appleId.id,
        data: updateParams,
      })
    }
  }

  const publishBlueNumber = () => {
    if (appleId) triggerUpdateAppleId({ is_published: true })
  }

  const unpublishBlueNumber = () => {
    if (appleId) triggerUpdateAppleId({ is_published: false })
  }

  const handleCancelBlueNumber = () => {
    if (!user?.id) {
      toast.error('User ID not found', {
        style: {
          background: '#343a40',
          color: '#fff',
          border: '1px solid #495057',
        },
      })
      return
    }

    if (
      window.confirm(
        'Are you sure you want to cancel this blue number? This will create a cancel ticket in HubSpot.'
      )
    ) {
      createCancelTicket.mutate({ user_id: user.id })
    }
  }

  const UserDetails = {
    service: service,
    phoneNumber: appleId ? appleId.phone_number : worknumber?.number,
    createdAt: appleId
      ? moment(appleId.created_at).format('LLL')
      : worknumber?.created_at
      ? moment(worknumber.created_at).format('LLL')
      : 'N/A',
    responseRate:
      appleIdDetails?.response_rate !== undefined &&
      appleIdDetails?.response_rate !== null
        ? `${appleIdDetails.response_rate}%`
        : 'N/A',
    callRecording:
      callRecordingEnabled !== undefined
        ? callRecordingEnabled
          ? 'Enabled'
          : 'Disabled'
        : 'N/A',
    forwardingNumber: appleIdDetails
      ? appleIdDetails.forwarding_number
      : worknumber?.forwarding_number,
  }

  return (
    <Col md="12">
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Phone Details</h4>
          {!worknumber && !appleId ? (
            <Dropdown className="user-actions-dropdown">
              <Dropdown.Toggle
                variant="link"
                className="p-1 text-success text-decoration-none d-flex align-items-center fw-medium shadow-none"
                id="phone-actions-dropdown"
                style={{ border: 'none' }}
                bsPrefix="btn"
              >
                Actions
                <ion-icon
                  name="chevron-down-outline"
                  className="ms-2"
                  style={{ fontSize: '14px' }}
                />
              </Dropdown.Toggle>

              <Dropdown.Menu className="bg-dark border-success border-2 rounded">
                <Dropdown.Item
                  className="text-white px-3 py-2"
                  onClick={handleProvisionNumber}
                  disabled={provisionTwilioNumber.isLoading}
                >
                  {provisionTwilioNumber.isLoading
                    ? 'Provisioning...'
                    : 'Provision Twilio Number'}
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-white px-3 py-2"
                  onClick={handleReactivatePhoneNumber}
                >
                  Reactivate Twilio Number
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : !appleId && worknumber ? (
            <Dropdown className="user-actions-dropdown">
              <Dropdown.Toggle
                variant="link"
                className="p-1 text-success text-decoration-none d-flex align-items-center fw-medium shadow-none"
                id="phone-actions-dropdown"
                style={{ border: 'none' }}
                bsPrefix="btn"
              >
                Actions
                <ion-icon
                  name="chevron-down-outline"
                  className="ms-2"
                  style={{ fontSize: '14px' }}
                />
              </Dropdown.Toggle>

              <Dropdown.Menu className="bg-dark border-success border-2 rounded">
                {isInternalAdmin ? (
                  <Dropdown.Item
                    className="text-white px-3 py-2"
                    onClick={handleTwilioVoiceIntegrity}
                  >
                    Twilio Voice Integrity
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item
                  className="text-danger px-3 py-2"
                  onClick={() => handleDeletePhoneNumber(worknumber.id)}
                  disabled={deletePhoneNumber.isLoading}
                >
                  Delete Phone Number
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : null}
        </Card.Header>
        <Card.Body>
          {appleId || worknumber ? (
            <>
              {/* Phone Info Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="fw-bold mb-1">Linq Phone Number</div>
                    <div className="d-flex align-items-center">
                      <span className="me-2">
                        {appleId ? (
                          <span style={{ color: '#10B981' }}>
                            {appleId.phone_number}
                          </span>
                        ) : (
                          <span>{worknumber?.number || 'N/A'}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="fw-bold mb-1">Service</div>
                    <div
                      style={{
                        color: service === 'Linq Blue' ? '#4da6ff' : '#10B981',
                      }}
                    >
                      {service}
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="fw-bold">Created on</div>
                    <div>{UserDetails.createdAt}</div>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="mb-2">
                    <div className="fw-bold">Forwarding Number</div>
                    <div>{UserDetails.forwardingNumber || 'N/A'}</div>
                  </div>
                  <div className="mb-2">
                    <div className="fw-bold">Call Recording</div>
                    <div>{UserDetails.callRecording}</div>
                  </div>
                  <div className="mb-2">
                    <div className="fw-bold">Response Rate (Last 7 Days)</div>
                    <div>{UserDetails.responseRate}</div>
                  </div>
                </div>
                {/* Action Buttons */}
                {(appleId || worknumber) && (
                  <div className="d-flex gap-2 mb-4">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="px-3 py-1 rounded-3"
                      style={{
                        borderColor: blockGhostMessages ? '#999999' : '#4da6ff',
                        color: blockGhostMessages ? '#999999' : '#4da6ff',
                        minWidth: '120px',
                        borderWidth: '2px',
                        opacity: blockGhostMessages ? 0.6 : 1,
                        cursor: blockGhostMessages ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        if (!blockGhostMessages) {
                          e.currentTarget.style.color = 'white'
                          e.currentTarget.style.backgroundColor = '#4da6ff'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!blockGhostMessages) {
                          e.currentTarget.style.color = '#4da6ff'
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }
                      }}
                      disabled={blockGhostMessages}
                      onClick={() =>
                        history.push(`/user/${user?.id}/chat_messages`)
                      }
                    >
                      View Messages
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="px-3 py-1 rounded-3"
                      style={{
                        borderColor: '#10B981',
                        color: '#10B981',
                        minWidth: '150px',
                        borderWidth: '2px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'white'
                        e.currentTarget.style.backgroundColor = '#10B981'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#10B981'
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                      onClick={() =>
                        history.push(`/user/${user?.id}/panopticon-profile`)
                      }
                    >
                      View Panopticon Profile
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : isLoadingSubscription ? (
            <p>Loading...</p>
          ) : subscriptionData?.linq_blue.present &&
            !subscriptionData?.isBlueNumberAvailable &&
            subscriptionData?.active_linq_blue_numbers !==
              subscriptionData?.linq_blue.quantity ? (
            <>
              <TextInformationLine
                label="Status"
                data="Restricted from claiming a Linq One number."
              />
              <TextInformationLine
                label="Reason"
                data="Organization's purchased Linq Blue lines are unassigned or not provisioned yet!"
              />
              <TextInformationLine
                label="Org Linq Blue stats"
                data={`${subscriptionData?.active_linq_blue_numbers} active out of ${subscriptionData?.linq_blue.quantity}`}
              />
            </>
          ) : (
            <>
              <TextInformationLine label="Service" data="Linq One" />
              <TextInformationLine
                label="Status"
                data="Number not claimed yet!"
              />
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modals */}
      {showReactivateModal && (
        <ReactivatePhoneNumberModal
          show={showReactivateModal}
          onHide={() => setShowReactivateModal(false)}
          userId={user?.id || ''}
          userName={userName}
        />
      )}
    </Col>
  )
}

export default UserPhoneDetailsCardNew
